import React, { useEffect, useState } from "react";
import {
  ImageIcon,
  X,
  Eye,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ArrowLeft
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../contexts/NotificationContext";

const AdminApprovalGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [activeTab, setActiveTab] = useState("new"); // "new" or "history"
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [previousNewRequestsCount, setPreviousNewRequestsCount] = useState(0);
  const itemsPerPage = 8;

  // Animation states
  const [animatePage, setAnimatePage] = useState(false);

  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  useEffect(() => {
    setAnimatePage(true);
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        "https://aaapd.org.et/gallery_backend/api/getGalleryImages.php"
      );
      if (Array.isArray(res.data)) {
        setImages(res.data);
      } else {
        setImages([]);
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      showMessage("Failed to fetch images.", "error");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, type = "success") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(""), 5000);
  };

  const handleApprove = async (id) => {
    try {
      const res = await axios.post(
        "https://aaapd.org.et/gallery_backend/api/approveImage.php",
        { id: id },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      showMessage(res.data.message || "Image approved successfully");

      // Trigger notification for approval
      addNotification({
        type: 'success',
        title: 'Gallery Request Approved',
        message: 'A gallery image has been successfully approved',
        data: { action: 'approved', imageId: id }
      });

      fetchImages();
    } catch (error) {
      console.error("Error approving image:", error);
      showMessage(error.response?.data?.message || "Error approving image.", "error");
    }
  };

  const handleReject = async (id) => {
    try {
      const res = await axios.post(
        "https://aaapd.org.et/gallery_backend/api/rejectImage.php",
        { id: id },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      showMessage(res.data.message || "Image rejected successfully");

      // Trigger notification for rejection
      addNotification({
        type: 'warning',
        title: 'Gallery Request Rejected',
        message: 'A gallery image has been rejected',
        data: { action: 'rejected', imageId: id }
      });

      fetchImages();
    } catch (error) {
      console.error("Error rejecting image:", error);
      showMessage(error.response?.data?.message || "Error rejecting image.", "error");
    }
  };

  const newRequests = images.filter((img) => img.status === "pending");
  const history = images.filter((img) => img.status !== "pending");

  const totalPages = Math.ceil(history.length / itemsPerPage);
  const paginatedHistory = history.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div
      className={`bg-gray-50 min-h-screen p-6 transition-opacity duration-1000 ease-in-out ${
        animatePage ? "opacity-100" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/galleryadmin')}
              className="p-2 hover:bg-white rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Gallery Approvals</h1>
              <p className="text-gray-600 mt-1">Review and approve gallery image submissions</p>
            </div>
          </div>
        </header>

        {/* Message */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              messageType === "error"
                ? "bg-red-100 border-l-4 border-red-500 text-red-700"
                : "bg-green-100 border-l-4 border-green-500 text-green-700"
            }`}
          >
            <div className="flex items-center gap-2">
              {messageType === "error" ? (
                <AlertCircle className="w-5 h-5" />
              ) : (
                <CheckCircle className="w-5 h-5" />
              )}
              {message}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
              activeTab === "new"
                ? "bg-blue-600 text-white shadow-md"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
            onClick={() => setActiveTab("new")}
          >
            New Requests ({newRequests.length})
          </button>
          <button
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
              activeTab === "history"
                ? "bg-blue-600 text-white shadow-md"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
            onClick={() => setActiveTab("history")}
          >
            History ({history.length})
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="flex items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
              <p className="text-gray-600">Loading gallery requests...</p>
            </div>
          </div>
        ) : (
          <div>
            {activeTab === "new" && (
              <>
                {newRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No new requests</h3>
                    <p className="mt-1 text-sm text-gray-500">No gallery images awaiting approval.</p>
                  </div>
                ) : (
                  <div className="bg-white shadow overflow-hidden rounded-lg">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Preview
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Alt Text
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Description
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Author
                            </th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {newRequests.map((img) => (
                            <tr key={img.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex-shrink-0 h-16 w-16">
                                  <img
                                    className="h-16 w-16 rounded-md object-cover border border-gray-200"
                                    src={img.src}
                                    alt={img.alt_text || 'Gallery image'}
                                  />
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                                  {img.alt_text || <span className="text-gray-400">No alt text</span>}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-500 max-w-md truncate">
                                  {img.description || <span className="text-gray-400">No description</span>}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">{img.author || <span className="text-gray-400">N/A</span>}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={() => handleApprove(img.id)}
                                    className="bg-green-600 hover:bg-green-700 text-white py-1.5 px-3 rounded-md transition-colors duration-200 flex items-center gap-1"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                    Approve
                                  </button>
                                  <button
                                    onClick={() => handleReject(img.id)}
                                    className="bg-red-600 hover:bg-red-700 text-white py-1.5 px-3 rounded-md transition-colors duration-200 flex items-center gap-1"
                                  >
                                    <AlertCircle className="w-4 h-4" />
                                    Reject
                                  </button>
                                  <button
                                    onClick={() => setSelectedImage(img)}
                                    className="text-blue-600 hover:text-blue-900 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
                                    title="View Details"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === "history" && (
              <>
                {history.length === 0 ? (
                  <div className="text-center py-12">
                    <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No history records</h3>
                    <p className="mt-1 text-sm text-gray-500">No processed gallery requests found.</p>
                  </div>
                ) : (
                  <>
                    <div className="bg-white shadow overflow-hidden rounded-lg">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Preview
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Alt Text
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Description
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Author
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedHistory.map((img) => (
                              <tr key={img.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex-shrink-0 h-16 w-16">
                                    <img
                                      className="h-16 w-16 rounded-md object-cover border border-gray-200"
                                      src={img.src}
                                      alt={img.alt_text || 'Gallery image'}
                                    />
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                                    {img.alt_text || <span className="text-gray-400">No alt text</span>}
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="text-sm text-gray-500 max-w-md truncate">
                                    {img.description || <span className="text-gray-400">No description</span>}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-500">{img.author || <span className="text-gray-400">N/A</span>}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    img.status === "approved"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                  }`}>
                                    {img.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={() => setSelectedImage(img)}
                                    className="text-blue-600 hover:text-blue-900 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
                                    title="View Details"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="bg-white px-6 py-3 flex items-center justify-between border-t border-gray-200">
                          <div className="flex-1 flex justify-between sm:hidden">
                            <button
                              onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                              disabled={currentPage === 1}
                              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                currentPage === 1
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              Previous
                            </button>
                            <button
                              onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                currentPage === totalPages
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              Next
                            </button>
                          </div>
                          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                              <p className="text-sm text-gray-700">
                                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                                <span className="font-medium">
                                  {Math.min(currentPage * itemsPerPage, history.length)}
                                </span>{' '}
                                of <span className="font-medium">{history.length}</span> results
                              </p>
                            </div>
                            <div>
                              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <button
                                  onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                                  disabled={currentPage === 1}
                                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                                    currentPage === 1
                                      ? 'text-gray-300 cursor-not-allowed'
                                      : 'text-gray-500 hover:bg-gray-50'
                                  }`}
                                >
                                  <span className="sr-only">Previous</span>
                                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                                </button>

                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                  let pageNum;
                                  if (totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                  } else {
                                    pageNum = currentPage - 2 + i;
                                  }

                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => setCurrentPage(pageNum)}
                                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                        currentPage === pageNum
                                          ? 'z-10 bg-teal-50 border-teal-500 text-teal-600'
                                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                      }`}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}

                                <button
                                  onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                                  disabled={currentPage === totalPages}
                                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                                    currentPage === totalPages
                                      ? 'text-gray-300 cursor-not-allowed'
                                      : 'text-gray-500 hover:bg-gray-50'
                                  }`}
                                >
                                  <span className="sr-only">Next</span>
                                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                                </button>
                              </nav>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Modal for Image Details */}
        {selectedImage && (
          <div
            className="fixed inset-0 flex items-center justify-center z-50"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            }}
          >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4">
              <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <Eye className="w-4 h-4 text-teal-600" />
                  Image Details
                </h3>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-5 space-y-3">
                <div className="flex justify-center bg-gray-50 rounded-lg p-2">
                  <img
                    src={selectedImage.src}
                    alt={selectedImage.alt_text || "Gallery image"}
                    className="max-h-32 max-w-full object-contain rounded border border-gray-200"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Alt Text
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-2 py-1.5 rounded border border-gray-200">
                    {selectedImage.alt_text || (
                      <span className="text-gray-400 italic">No alt text</span>
                    )}
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Description
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-2 py-1.5 rounded border border-gray-200 min-h-[50px]">
                    {selectedImage.description || (
                      <span className="text-gray-400 italic">No description</span>
                    )}
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Submitted By
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-2 py-1.5 rounded border border-gray-200">
                    {selectedImage.author || <span className="text-gray-400 italic">N/A</span>}
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Status
                  </label>
                  <p
                    className={`text-sm font-semibold bg-gray-50 px-2 py-1.5 rounded border border-gray-200 ${
                      selectedImage.status === "approved"
                        ? "text-green-600"
                        : selectedImage.status === "rejected"
                        ? "text-red-600"
                        : "text-yellow-600"
                    }`}
                  >
                    {selectedImage.status}
                  </p>
                </div>

                <div className="pt-3 flex justify-end">
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="inline-flex justify-center py-1.5 px-3 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminApprovalGallery;
